import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import api from "../services/api"; // axios instance in your project
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

// TeachersPage.jsx
// Modern, RTL-ready, responsive CRUD page for Teacher management

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.04 } },
};
const itemVariants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } };

const emptyTeacher = { fullName: "", type: "fixed", monthlySalary: "", hourlyRate: "", notes: "" };

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(8);
  const [totalPages, setTotalPages] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyTeacher);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: "" });

  const mountedRef = useRef(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    fetchTeachers(1, query);
    return () => (mountedRef.current = false);
  }, []);

  // fetch teachers with pagination & search
  const fetchTeachers = useCallback(async (p = 1, q = "") => {
    setLoading(true);
    try {
      const params = { page: p, limit: pageSize };
      if (q && q.trim() !== "") params.search = q.trim();
      const { data } = await api.get("/teacher", { params });
      if (!mountedRef.current) return;
      // Expect API: { items: [...], total: 123, page: 1, totalPages: N }
      const items = data.items || data;
      const total = data.total || (Array.isArray(items) ? items.length : 0);
      const tPages = data.totalPages || Math.max(1, Math.ceil(total / pageSize));
      setTeachers(items);
      setPage(data.page || p);
      setTotalPages(tPages);
    } catch (err) {
      console.error("fetchTeachers:", err);
      toast.error(err?.response?.data?.error || "فشل في جلب المدرسين");
      setTeachers([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [pageSize]);

  // search debounced
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchTeachers(1, query), 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchTeachers]);

  // open add modal
  const openAdd = () => {
    setForm(emptyTeacher);
    setIsEdit(false);
    setEditingId(null);
    setModalOpen(true);
  };

  // open edit modal
  const openEdit = (t) => {
    setForm({
      fullName: t.fullName || "",
      type: t.type || "fixed",
      monthlySalary: t.monthlySalary ?? "",
      hourlyRate: t.hourlyRate ?? "",
      notes: t.notes || "",
    });
    setIsEdit(true);
    setEditingId(t._id);
    setModalOpen(true);
  };

  // handle create / update
  const saveTeacher = async () => {
    // basic validation
    if (!form.fullName || form.fullName.trim().length < 3) {
      toast.error("الرجاء إدخال اسم كامل صالح للمدرس");
      return;
    }
    if (form.type === "fixed") {
      if (!form.monthlySalary || Number(form.monthlySalary) <= 0) {
        toast.error("الرجاء إدخال مرتب شهري صالح للمدرس بنظام المرتب الثابت");
        return;
      }
    } else {
      if (!form.hourlyRate || Number(form.hourlyRate) <= 0) {
        toast.error("الرجاء إدخال سعر الساعة صالح للمدرس بنظام الساعة");
        return;
      }
    }

    try {
      if (isEdit && editingId) {
        const payload = {
          fullName: form.fullName.trim(),
          type: form.type,
          monthlySalary: form.type === "fixed" ? Number(form.monthlySalary) : undefined,
          hourlyRate: form.type === "hourly" ? Number(form.hourlyRate) : undefined,
          notes: form.notes || "",
        };
        const { data } = await api.put(`/teacher/${editingId}`, payload);
        toast.success("تم تحديث بيانات المدرس");
      } else {
        const payload = {
          fullName: form.fullName.trim(),
          type: form.type,
          monthlySalary: form.type === "fixed" ? Number(form.monthlySalary) : undefined,
          hourlyRate: form.type === "hourly" ? Number(form.hourlyRate) : undefined,
          notes: form.notes || "",
        };
        const { data } = await api.post(`/teacher`, payload);
        toast.success("تم إضافة المدرس بنجاح");
      }

      setModalOpen(false);
      fetchTeachers(page, query);
    } catch (err) {
      console.error("saveTeacher:", err);
      toast.error(err?.response?.data?.error || "فشل في حفظ بيانات المدرس");
    }
  };

  const requestDelete = (id, name) => setConfirmDelete({ open: true, id, name });

  const doDelete = async () => {
    try {
      await api.delete(`/teacher/${confirmDelete.id}`);
      toast.success("تم حذف المدرس");
      setConfirmDelete({ open: false, id: null, name: "" });
      // refresh list
      fetchTeachers(1, query);
    } catch (err) {
      console.error("deleteTeacher:", err);
      toast.error(err?.response?.data?.error || "فشل في حذف المدرس");
    }
  };

  return (
    <div dir="rtl" className="min-h-screen p-6 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-1000">إدارة المدرّسين</h1>
            <p className="text-slate-800 mt-1"> إضافة وتعديل وحذف وبحث سريع.</p>
          </div>

          <div className="flex gap-3 items-center">
            <div className="relative flex items-center">
              <Input
                id="teacherSearch"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ابحث بالاسم ... "
                className="pl-10 pr-3"
              />
              <span className="absolute left-3 top-3.5 text-slate-400"><MagnifyingGlassIcon className="h-5 w-5" /></span>
            </div>

            <Button onClick={openAdd} className="flex items-center gap-2">
              <PlusCircleIcon className="h-5 w-5" />
              إضافة مدرس
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">

            {/* Count */}
            <Card className="p-4">
              <div className="text-lg font-bold text-slate-900">عدد المدرّسين :</div>
              <div className="text-2xl  text-green-700">{teachers.length}</div>
            </Card>

    

       

          </motion.div>
          <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
              <div className="text-lg font-semibold">قائمة المدرّسين</div>
            </div>
<div className="overflow-x-auto">
  <table className="min-w-full text-right border-separate border-spacing-y-1 px-2">
    <thead className="bg-slate-100 text-slate-900">
      <tr>
        <th className="p-3 text-lg font-bold">الاسم الكامل</th>
        <th className="p-3 text-lg font-bold">نوع المدرّس</th>
        <th className="p-3 text-lg font-bold">الراتب الشهري</th>
        <th className="p-3 text-lg font-bold">ملاحظات</th>
        <th className="p-3 text-lg font-bold">الإجراءات</th>
      </tr>
    </thead>

    <tbody>
      {teachers.map((t) => (
        <tr
          key={t._id}
          className="bg-white shadow-sm hover:shadow-md transition-all rounded-xl"
        >
          {/* الاسم */}
          <td className="p-4 font-semibold text-slate-800 whitespace-nowrap">
            {t.fullName}
          </td>

          {/* النوع */}
          <td className="p-4 text-slate-700 whitespace-nowrap">
            <span
              className={`px-3 py-1 rounded-full text-sm font-bold ${
                t.type === "fixed"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {t.type === "fixed" ? "ثابت" : "ساعات"}
            </span>
          </td>

          {/* الراتب */}
          <td className="p-4 text-green-700 font-semibold">
            {t.type === "fixed"
              ? t.monthlySalary
                ? Number(t.monthlySalary).toLocaleString() + " ل.س / شهر"
                : "-"
              : t.hourlyRate
              ? Number(t.hourlyRate).toLocaleString() + " ل.س / ساعة"
              : "-"}
          </td>

          {/* الملاحظات */}
          <td className="p-4 text-slate-500 max-w-xs truncate">
            {t.notes || "-"}
          </td>

          {/* الأفعال */}
          <td className="p-4 whitespace-nowrap">
            <div className="flex justify-center gap-3">
              <button
                onClick={() => openEdit(t)}
                className="p-2 rounded-full bg-blue-50 hover:bg-blue-100 transition"
                title="تعديل"
              >
                <PencilSquareIcon className="h-5 w-5 text-blue-600" />
              </button>

              <button
                onClick={() => requestDelete(t._id, t.fullName)}
                className="p-2 rounded-full bg-red-50 hover:bg-red-100 transition"
                title="حذف"
              >
                <TrashIcon className="h-5 w-5 text-red-600" />
              </button>
            </div>
          </td>
        </tr>
      ))}

      {teachers.length === 0 && !loading && (
        <tr>
          <td
            colSpan={5}
            className="p-12 text-center text-slate-500"
          >
            لا توجد بيانات
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

            <div className="p-4 flex items-center justify-between border-t bg-white">
              <div className="text-sm text-slate-600">الصفحات</div>
              <div className="flex items-center gap-2">
                <Button onClick={() => { if (page > 1) { setPage(p => { const np = p - 1; fetchTeachers(np, query); return np; }); } }} className="!px-3 !py-2">
                  <ChevronLeftIcon className="h-5 w-5" />
                </Button>
                <div className="text-sm font-medium">{page} / {totalPages}</div>
                <Button onClick={() => { if (page < totalPages) { setPage(p => { const np = p + 1; fetchTeachers(np, query); return np; }); } }} className="!px-3 !py-2">
                  <ChevronRightIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Modal: Add / Edit Teacher */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setModalOpen(false)} />

            <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }} className="relative z-10 w-full max-w-2xl">
              <Card className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold">{isEdit ? 'تعديل بيانات المدرّس' : 'إضافة مدرس جديد'}</h2>
                    <p className="text-slate-700 mt-1">املأ الحقول ثم اضغط إضافة</p>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => { setModalOpen(false); }} variant="ghost">إلغاء</Button>
                    <Button onClick={saveTeacher}>{isEdit ? 'حفظ التعديلات' : 'إضافة '}</Button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="اسم المدرّس الكامل" value={form.fullName} onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))} />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">النوع </label>
                    <select value={form.type} onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                      <option value="fixed">ثابت (راتب شهري)</option>
                      <option value="hourly">ساعات</option>
                    </select>
                  </div>

                  {form.type === 'fixed' ? (
                    <Input label="المرتب الشهري (ل.س)" type="number" value={form.monthlySalary} onChange={(e) => setForm(f => ({ ...f, monthlySalary: e.target.value }))} />
                  ) : (
                    <Input label="سعر الساعة (ل.س)" type="number" value={form.hourlyRate} onChange={(e) => setForm(f => ({ ...f, hourlyRate: e.target.value }))} />
                  )}

                  <Input label="ملاحظات" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {confirmDelete.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDelete({ open: false, id: null, name: "" })} />
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }} className="relative z-10 w-full max-w-md">
              <Card className="p-6">
                <h3 className="text-xl font-bold">تأكيد الحذف</h3>
                <p className="text-slate-800 mt-2">هل أنت متأكد من حذف المدرّس <span className="font-semibold">{confirmDelete.name}</span> ؟  سيتم حذف السجل نهائياً.</p>

                <div className="mt-4 flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setConfirmDelete({ open: false, id: null, name: "" })}>إلغاء</Button>
                  <Button variant="danger" onClick={doDelete}>حذف نهائي</Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

